import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { REPORT_REASON_OPTIONS, OTHER_REPORT_REASON_ID, reportService } from '@wlb/shared/api/services/reportService';
import feedService from '@wlb/shared/api/services/feedService';
import type { FeedPost } from '@wlb/shared/types/post';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { FeedLinesSkeleton } from './FeedModalSkeleton';
import './feedModal.css';

interface ReportPostModalProps {
  post: FeedPost | null;
  onClose: () => void;
  /** Called after a successful post report (e.g. Home feed resync). */
  onReported?: () => void;
}

export function ReportPostModal({ post, onClose, onReported }: ReportPostModalProps) {
  const { t } = useTranslation();
  const [reasons, setReasons] = useState(REPORT_REASON_OPTIONS);
  const [selectedReasonIds, setSelectedReasonIds] = useState<number[]>([]);
  const [otherDetails, setOtherDetails] = useState('');
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoadingReasons(true);
    reportService.getReportReasons()
      .then((data) => { if (alive) setReasons(data); })
      .catch(() => { if (alive) ToastService.show({ type: 'error', message: t('common.errors.generic') }); })
      .finally(() => { if (alive) setLoadingReasons(false); });
    return () => { alive = false; };
  }, [t]);

  const isOtherSelected = useMemo(() => selectedReasonIds.includes(OTHER_REPORT_REASON_ID), [selectedReasonIds]);
  const disabled = submitting || loadingReasons || selectedReasonIds.length === 0 || (isOtherSelected && !otherDetails.trim());

  const toggleReason = (reasonId: number) => {
    setSelectedReasonIds(current => current.includes(reasonId)
      ? current.filter(id => id !== reasonId)
      : [...current, reasonId]);
  };

  const handleSubmit = async () => {
    if (!post || selectedReasonIds.length === 0) return;
    setSubmitting(true);
    try {
      const selectedNormalReasonIds = selectedReasonIds.filter(id => id !== OTHER_REPORT_REASON_ID);
      const hasOther = isOtherSelected;

      const result = await feedService.reportPost({
        postId: post.id,
        reasonIds: selectedNormalReasonIds.length > 0 ? selectedNormalReasonIds : undefined,
        otherDescription: hasOther && otherDetails.trim() ? otherDetails.trim() : undefined,
      });

      if (!result.success) {
        throw new Error('report_post_failed');
      }
      onReported?.();
      setSubmitted(true);
    } catch {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
    } finally {
      setSubmitting(false);
    }
  };

  if (!post) return null;

  if (submitted) {
    return (
      <Modal title={t('profile.report.title')} onClose={onClose} labelledBy="report-post-success-title" className="report-post-modal">
        <div className="report-post-modal__success">
          <div className="report-post-modal__success-icon" aria-hidden="true">✓</div>
          <h3>{t('profile.report.thankYou.title')}</h3>
          <p>{t('profile.report.thankYou.subtitle')}</p>
          <Button fullWidth size="lg" onClick={onClose}>{t('common.buttons.done')}</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={t('profile.report.title')} onClose={onClose} labelledBy="report-post-title" className="report-post-modal">
      <div className="report-post-modal__content">
        <div className="report-post-modal__intro">
          <p className="report-post-modal__heading">{t('profile.report.steps.whatReportingPost')}</p>
          <p className="report-post-modal__subheading">{t('profile.report.steps.selectAllThatApply')}</p>
        </div>
        {loadingReasons ? (
          <FeedLinesSkeleton />
        ) : (
          <div className="report-post-modal__reasons" role="group" aria-label={t('profile.report.steps.whatReportingPost')}>
            {reasons.map((reason) => {
              const isSelected = selectedReasonIds.includes(reason.id);
              const isOther = reason.id === OTHER_REPORT_REASON_ID;
              return (
                <div key={reason.id} className="report-post-modal__reason-wrap">
                  <button
                    type="button"
                    className={`report-post-modal__reason${isSelected ? ' report-post-modal__reason--selected' : ''}`}
                    aria-checked={isSelected}
                    role="checkbox"
                    onClick={() => toggleReason(reason.id)}
                  >
                    <span className="report-post-modal__reason-indicator" aria-hidden="true">{isSelected ? '✓' : ''}</span>
                    <span className="report-post-modal__reason-label">{t(reason.labelKey)}</span>
                  </button>
                  {isOther && isSelected && (
                    <textarea
                      className="report-post-modal__details"
                      value={otherDetails}
                      onChange={event => setOtherDetails(event.target.value)}
                      placeholder={t('profile.report.detailsPlaceholder')}
                      rows={4}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
        <Button fullWidth size="lg" onClick={() => { handleSubmit().catch(() => undefined); }} disabled={disabled}>
          {t('profile.report.buttons.report')}
        </Button>
      </div>
    </Modal>
  );
}
